<!-- eslint-disable vue/no-v-html -->
<template>
  <utd-modal-dialog ref="emailVerificationDialog"
                    :modal-classes="'email-verification-modal'"
                    :close-on-overlay-click="false"
                    :close-on-escape="!isMobileOnDesktop"
                    @closed="handleClosed">
    <template #header>Verify Email</template>
    <div class="email-verification-dialog">
      <div v-if="isDisasterRecovery" class="dialog__bcdr">
        <p>{{ disasterExplanation }}</p>
        <p>{{ disasterApology }}</p>
      </div>
      <div v-else class="dialog__content">
        <p class="dialog__description">
          We just sent an email to {{ userEmail }}. The code is valid for 10 minutes.
        </p>
        <div class="dialog__code-input-container">
          <utd-verification-code-input ref="codeInput"
                                       v-model="verificationCode"
                                       @submit="submitVerificationCode()" />
          <div v-if="verificationError"
               class="dialog__error-notification"
               role="alert"
               aria-live="assertive">
            <span class="dialog__error-icon wkce-icon-filled-stop-sign"
                  aria-hidden="true" />
            <div class="dialog__error-copy">
              <p class="dialog__error-title">Error</p>
              <p class="dialog__error-message"
                 role="button"
                 tabindex="0"
                 @click="handleErrorMessageClick($event)"
                 @keydown.enter.prevent="handleErrorMessageClick($event)"
                 @keydown.space.prevent="handleErrorMessageClick($event)"
                 v-html="getErrorMessage(verificationError)" />
            </div>
          </div>
          <p class="dialog__code-helper">
            Enter the 6 digit code that appears in your email
          </p>
        </div>
      </div>
    </div>
    <template #footer>
      <utd-button v-if="isDisasterRecovery"
                  id="closeEmailVerificationButton"
                  @click="closeDialog()">Close</utd-button>
      <span v-else class="dialog__resend-container">
        <a v-if="!isResendDisabled"
           id="resendEmailLink"
           href="#"
           class="dialog__resend-link"
           @click.prevent="sendVerificationCode()">Re-send Email</a>
        <span v-else
              class="dialog__resend-countdown"
              role="status"
              aria-label="Sending Email. Re-send Email will be available soon.">
          <span class="dialog__resend-countdown-text">Sending Email</span>
          <span class="dialog__resend-ring"
                :style="{ '--cooldown-progress': `${resendCooldownProgress}%` }"
                aria-hidden="true" />
        </span>
      </span>
      <utd-button v-if="!isDisasterRecovery"
                  id="verifyEmailButton"
                  :disabled="isSubmitDisabled"
                  @click="submitVerificationCode()">Verify</utd-button>
    </template>
  </utd-modal-dialog>
</template>

<script setup>
import {
    computed,
    nextTick,
    onBeforeUnmount,
    ref
} from 'vue';
import { useStore } from 'vuex';
import UtdButton from '_acaSrc/components/shared/stdlib/UtdButton.vue';
import UtdModalDialog from '_acaSrc/components/shared/stdlib/UtdModalDialog.vue';
import UtdVerificationCodeInput from
    '_acaSrc/components/shared/stdlib/UtdVerificationCodeInput.vue';
import PubSub from '_acaSrc/utility/PubSub';
import utdRest from '_acaSrc/utility/http/UtdRestHooks';
import { SET_EMAIL_VERIFIED } from '_acaSrc/store/profile.store';
import { setQueryParamValueNoReload } from '_acaSrc/utility/Browsers';
import { BCDR_MESSAGE, EMAIL_VERIFIED_PARAM } from '_acaSrc/utility/constants';

const RESEND_COOLDOWN_SECONDS = 10;
const RESEND_EMAIL_LINK_TEXT = 'Re-send Email';
const disasterExplanation = BCDR_MESSAGE.DEFAULT;
const disasterApology = BCDR_MESSAGE.APOLOGY;

const store = useStore();
const pubsub = new PubSub();

const emailVerificationDialog = ref(null);
const codeInput = ref(null);
const verificationCode = ref('');
const isSubmitting = ref(false);
const isSending = ref(false);
const shouldCloseMyAccountDialog = ref(true);
const resendCooldownIntervalId = ref(null);
const resendCooldownSeconds = ref(0);
const sendVerificationRequestId = ref(0);
const shouldShowSuccessToast = ref(false);
const verificationError = ref('');

const isMobileOnDesktop = computed(() => store.getters['device/isMobileOnDesktop']);
const isDisasterRecovery = computed(() => store.getters['app/isDisasterRecovery']);
const userEmail = computed(() => store.getters['profile/userEmail']);
const isSubmitDisabled = computed(() => verificationCode.value.length !== 6
  || isSubmitting.value);
const isResendDisabled = computed(() => isSending.value || resendCooldownSeconds.value > 0);
const resendCooldownProgress = computed(() => {
    if (!resendCooldownSeconds.value) {
        return 0;
    }
    return (resendCooldownSeconds.value / RESEND_COOLDOWN_SECONDS) * 100;
});

onBeforeUnmount(() => {
    sendVerificationRequestId.value += 1;
    clearResendCooldown();
});

function open(closeMyAccountDialog = true) {
    resetForm();
    shouldCloseMyAccountDialog.value = closeMyAccountDialog;
    emailVerificationDialog.value?.open();
    if (isDisasterRecovery.value) {
        return;
    }
    sendVerificationCode();
    nextTick(() => codeInput.value?.focus());
}

function closeDialog() {
    emailVerificationDialog.value?.close();
    resetForm();
}

function handleClosed() {
    if (shouldCloseMyAccountDialog.value) {
        pubsub.publish('wkutd.closeMyAccountDialog');
    }
    if (shouldShowSuccessToast.value) {
        setQueryParamValueNoReload(EMAIL_VERIFIED_PARAM, 'true');
        pubsub.publish('showEmailVerifiedToast');
    }
    resetForm();
}

function isActiveSendRequest(requestId) {
    return requestId === sendVerificationRequestId.value;
}

async function sendVerificationCode() {
    if (isDisasterRecovery.value || isResendDisabled.value) {
        return;
    }
    const requestId = sendVerificationRequestId.value + 1;
    sendVerificationRequestId.value = requestId;
    isSending.value = true;
    verificationError.value = '';
    try {
        await utdRest('myAccount/sendEmailVerification');
        if (isActiveSendRequest(requestId)) {
            startResendCooldown();
        }
    }
    catch {
        if (isActiveSendRequest(requestId)) {
            verificationError.value = 'Failed to send verification email. Please try again.';
        }
    }
    finally {
        if (isActiveSendRequest(requestId)) {
            isSending.value = false;
        }
    }
}

async function submitVerificationCode() {
    if (isDisasterRecovery.value || isSubmitting.value) {
        return;
    }
    isSubmitting.value = true;
    verificationError.value = '';
    try {
        const response = await utdRest('myAccount/verifyEmail', {
            code: verificationCode.value
        });
        const res = response.data || response;
        if (res.isValid) {
            handleVerificationSuccess();
        }
        else {
            verificationError.value = res.errorMessage
                || 'Verification failed. Please check your code and try again.';
        }
    }
    catch {
        verificationError.value = 'Verification failed. Please check your code and '
            + 'try again.';
    }
    finally {
        isSubmitting.value = false;
    }
}

function handleVerificationSuccess() {
    store.commit(`profile/${SET_EMAIL_VERIFIED}`, true);
    shouldShowSuccessToast.value = true;
    emailVerificationDialog.value?.close();
}

function startResendCooldown() {
    clearResendCooldown();
    resendCooldownSeconds.value = RESEND_COOLDOWN_SECONDS;
    resendCooldownIntervalId.value = window.setInterval(() => {
        if (resendCooldownSeconds.value <= 1) {
            clearResendCooldown();
            return;
        }
        resendCooldownSeconds.value -= 1;
    }, 1000);
}

function clearResendCooldown() {
    if (resendCooldownIntervalId.value) {
        window.clearInterval(resendCooldownIntervalId.value);
    }
    resendCooldownIntervalId.value = null;
    resendCooldownSeconds.value = 0;
}

function getErrorMessage(errorMessage) {
    if (!errorMessage) {
        return '';
    }

    if (!errorMessage.includes(RESEND_EMAIL_LINK_TEXT)) {
        return errorMessage;
    }

    return errorMessage
        .split(RESEND_EMAIL_LINK_TEXT)
        .join('<a href="#" class="dialog__resend-link">Re-send Email</a>');
}

function handleErrorMessageClick(event) {
    const resendLink = event.target?.closest?.('a.dialog__resend-link');
    if (!resendLink || resendLink.textContent !== RESEND_EMAIL_LINK_TEXT) {
        return;
    }

    event.preventDefault();
    sendVerificationCode({ isResend: true });
}

function resetForm() {
    sendVerificationRequestId.value += 1;
    clearResendCooldown();
    verificationCode.value = '';
    codeInput.value?.reset();
    isSubmitting.value = false;
    isSending.value = false;
    shouldCloseMyAccountDialog.value = true;
    shouldShowSuccessToast.value = false;
    verificationError.value = '';
}

defineExpose({
    open
});
</script>

<style lang="less">
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

@email-verification-mobile-max-width: 768px;

.email-verification-modal {
  .utd-dialog-content {
    .ds1-pa-3();
  }

  .utd-dialog-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: calc(@ds1-spacing-multiplier * 2);
  }
}

@media screen and (min-width: 768px) {
  .email-verification-modal {
    .utd-modal-content .utd-dialog-frame {
      width: 670px;
    }
  }
}

.email-verification-dialog {
  .dialog__description {
    .ds1-mt-0();
    .ds1-utd-size-3();
    color: @WKCE-GRAY-SHADE2;
    line-height: 1.4;
    overflow-wrap: anywhere;
  }

  .dialog__code-input-container {
    width: 100%;
    text-align: center;
  }

  .dialog__error-notification {
    .ds1-pa-2();
    .ds1-mt-1();
    display: flex;
    box-sizing: border-box;
    width: 100%;
    max-width: 340px;
    min-height: 34px;
    margin-right: auto;
    margin-left: auto;
    border: 1px solid @DS1-UTD-ERROR;
    border-left-width: 4px;
    text-align: left;
  }

  .dialog__error-icon {
    .ds1-mr-2();
    color: @DS1-UTD-ERROR;
    font-size: 16px;
    line-height: 18px;
  }

  .dialog__error-copy {
    min-width: 0;
  }

  .dialog__error-title {
    .ds1-ma-0();
    .ds1-mb-1();
    .ds1-utd-size-2();
    line-height: 18px;
    font-weight: 700;
  }

  .dialog__error-message {
    .ds1-ma-0();
    .ds1-utd-size-2();
    line-height: 18px;
  }

  .dialog__code-helper {
    .ds1-mb-0();
  }
}

.dialog__resend-container {
  display: inline-flex;
  align-items: center;
}

.dialog__resend-link {
  .ds1-utd-size-2();
  color: @WKCE-BLUE;
}

.dialog__resend-countdown {
  .ds1-utd-size-2();
  display: inline-flex;
  align-items: center;
  gap: 0.25em;
  color: @WKCE-GRAY-SHADE1;
  white-space: nowrap;
}

.dialog__resend-ring {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background:
    conic-gradient(
      transparent calc(100% - var(--cooldown-progress)),
      @WKCE-BLACK 0
    );
  transition: background 1s linear;

  &::after {
    display: block;
    width: 12px;
    height: 12px;
    margin: 2px;
    border-radius: 50%;
    background: @DS1-UTD-GRAY-BG-COLOR;
    content: '';
  }
}

@media screen and (max-width: @email-verification-mobile-max-width) {
  .email-verification-modal {
    .utd-modal-content .utd-dialog-frame {
      width: 100%;
    }

    .utd-dialog-content {
      overflow-y: visible;
    }
  }

}
</style>
